// How We Do It / Elakay Academy -- search engine and share metadata of the
// public pages (title, description, keywords, canonical, Open Graph,
// Twitter Card, JSON-LD Article), from the tutorial's own seo.* fields with
// the title / short description as fallbacks.
//
// The site is rendered in the browser: these tags are written into the live
// document (search engines that run JavaScript read them). Link-preview
// crawlers that do not run JavaScript get the same data from the server
// renderer (setupProxy.js + elakay_services/globalSocialPreviewService.js).
import { useEffect } from "react";
import { resolveHowWeDoItMediaUrl } from "./howWeDoItMediaUrl";

const SITE_NAME = "Elakay";
const OG_LOCALES = { en: "en_US", fr: "fr_FR", es: "es_ES", ht: "ht_HT", pt: "pt_BR" };

// Tags of block elements become a space, inline tags (<b>, <a>...) vanish:
// "complet <b>CJ</b>." stays "complet CJ." and French spacing ("Academy :")
// is left exactly as written.
const BLOCK_TAG = /<\/?(?:p|div|br|li|ul|ol|h[1-6]|tr|td|th|table|section|article|header|footer|blockquote|pre|hr)\b[^>]*>/gi;
const clean = (value, max = 300) =>
  String(value || "")
    .replace(BLOCK_TAG, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);

const shorten = (value, max = 200) => {
  const text = clean(value, 4000);
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), max - 40)).replace(/[\s.,;:!?-]+$/, "")}…`;
};

const absoluteUrl = (value, origin) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(resolveHowWeDoItMediaUrl(raw), origin);
    return /^https?:$/.test(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
};

const isPublicTutorial = (tutorial) =>
  tutorial?.status === "published" &&
  (!tutorial.visibility ||
    tutorial.visibility === "public" ||
    (tutorial.visibility === "selectedRoles" && !(Array.isArray(tutorial.allowedRoles) && tutorial.allowedRoles.length)));

/**
 * @param {object} tutorial normalized tutorial
 * @param {{origin?: string, preview?: boolean, fallbackDescription?: string}} options
 */
export const buildTutorialSeo = (tutorial, { origin = typeof window !== "undefined" ? window.location.origin : "https://www.elakay.com", preview = false, fallbackDescription = "" } = {}) => {
  if (!tutorial) return null;
  const title = clean(tutorial.seo?.title || tutorial.title, 160) || "How We Do It in Elakay";
  const description = shorten(tutorial.seo?.description || tutorial.shortDescription || fallbackDescription, 200);
  const keywords = (Array.isArray(tutorial.seo?.keywords) ? tutorial.seo.keywords : []).map((k) => clean(k, 60)).filter(Boolean).slice(0, 20);
  const canonical = `${origin}/how-we-do-it/${encodeURIComponent(tutorial.slug || "")}`;
  const image = absoluteUrl(tutorial.coverImage?.url, origin);
  const indexable = !preview && isPublicTutorial(tutorial);
  const language = tutorial.language || "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: title,
    ...(description ? { description } : {}),
    ...(image ? { image: [image] } : {}),
    ...(language ? { inLanguage: language } : {}),
    ...(tutorial.publishedAt ? { datePublished: new Date(tutorial.publishedAt).toISOString() } : {}),
    ...(tutorial.updatedAt ? { dateModified: new Date(tutorial.updatedAt).toISOString() } : {}),
    ...(keywords.length ? { keywords: keywords.join(", ") } : {}),
    ...(tutorial.category?.name ? { articleSection: clean(tutorial.category.name, 120) } : {}),
    mainEntityOfPage: canonical,
    publisher: { "@type": "Organization", name: SITE_NAME, url: origin },
  };
  return {
    documentTitle: `${title} | How We Do It | ${SITE_NAME}`,
    title,
    description,
    keywords,
    canonical,
    image,
    imageAlt: clean(tutorial.coverImage?.alt || title, 200),
    type: "article",
    locale: OG_LOCALES[language] || "",
    robots: indexable ? "index, follow" : "noindex, nofollow",
    publishedTime: tutorial.publishedAt ? new Date(tutorial.publishedAt).toISOString() : "",
    modifiedTime: tutorial.updatedAt ? new Date(tutorial.updatedAt).toISOString() : "",
    jsonLd: indexable ? jsonLd : null,
  };
};

export const buildTutorialsHomeSeo = ({ origin = typeof window !== "undefined" ? window.location.origin : "https://www.elakay.com", title, description } = {}) => ({
  documentTitle: `${clean(title, 120)} | ${SITE_NAME}`,
  title: clean(title, 160),
  description: shorten(description, 200),
  keywords: [],
  canonical: `${origin}/how-we-do-it`,
  image: "",
  imageAlt: clean(title, 200),
  type: "website",
  locale: "",
  robots: "index, follow",
  jsonLd: null,
});

// Writes the tags and returns a function restoring what was there before.
export const applyDocumentSeo = (seo, doc = typeof document !== "undefined" ? document : null) => {
  if (!seo || !doc) return () => {};
  const head = doc.head;
  const restorers = [];

  const previousTitle = doc.title;
  doc.title = seo.documentTitle || seo.title;
  restorers.push(() => { doc.title = previousTitle; });

  const upsert = (selector, create, attribute, value) => {
    let element = head.querySelector(selector);
    if (!value) {
      if (element && element.getAttribute("data-hwdi-seo") === "1") element.remove();
      return;
    }
    if (element) {
      const previous = element.getAttribute(attribute);
      element.setAttribute(attribute, value);
      restorers.push(() => { if (previous === null) element.removeAttribute(attribute); else element.setAttribute(attribute, previous); });
    } else {
      element = create();
      element.setAttribute(attribute, value);
      element.setAttribute("data-hwdi-seo", "1");
      head.appendChild(element);
      restorers.push(() => element.remove());
    }
  };
  const meta = (key, value, by = "name") =>
    upsert(`meta[${by}="${key}"]`, () => {
      const element = doc.createElement("meta");
      element.setAttribute(by, key);
      return element;
    }, "content", value);

  meta("description", seo.description);
  meta("keywords", (seo.keywords || []).join(", "));
  meta("robots", seo.robots);
  upsert('link[rel="canonical"]', () => {
    const element = doc.createElement("link");
    element.setAttribute("rel", "canonical");
    return element;
  }, "href", seo.canonical);
  meta("og:type", seo.type, "property");
  meta("og:site_name", SITE_NAME, "property");
  meta("og:title", seo.title, "property");
  meta("og:description", seo.description, "property");
  meta("og:url", seo.canonical, "property");
  meta("og:image", seo.image, "property");
  meta("og:image:alt", seo.image ? seo.imageAlt : "", "property");
  meta("og:locale", seo.locale, "property");
  meta("article:published_time", seo.publishedTime, "property");
  meta("article:modified_time", seo.modifiedTime, "property");
  meta("twitter:card", seo.image ? "summary_large_image" : "summary");
  meta("twitter:title", seo.title);
  meta("twitter:description", seo.description);
  meta("twitter:image", seo.image);

  if (seo.jsonLd) {
    const script = doc.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-hwdi-seo", "1");
    script.textContent = JSON.stringify(seo.jsonLd).replace(/</g, "\\u003c");
    head.appendChild(script);
    restorers.push(() => script.remove());
  }

  return () => restorers.reverse().forEach((restore) => restore());
};

// `refreshKey`: anything that makes the app re-set the route title
// (Layout's useElakayDocumentTitle runs on infoSource/language changes);
// applying on the next tick lets this page's own title win over it.
export const useDocumentSeo = (seo, refreshKey = null) => {
  const key = seo ? JSON.stringify(seo) : "";
  useEffect(() => {
    if (!seo) return undefined;
    let restore = null;
    const timer = setTimeout(() => {
      restore = applyDocumentSeo(seo);
    }, 0);
    return () => {
      clearTimeout(timer);
      if (restore) restore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshKey]);
};
